import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { Program, PaymentIntentResponse, DonationFormData, ApiError } from '../types';
import { decryptResponseData, encryptPayload } from '../utils/encryptDecrypt';
import { captureException } from '../utils/sentry';

// API URL - Note: No /v1 prefix as API routes are mounted at root
// Production API is on Heroku: https://gftl-api-live-ae0fa3ba64e6.herokuapp.com
// Staging API is: https://api-gftl.inverge.net
const API_URL = process.env.REACT_APP_API_URL || 'https://gftl-api-live-ae0fa3ba64e6.herokuapp.com';

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - encrypt request body
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    try {
      // Only encrypt POST/PUT/PATCH requests with data
      if (config.data && config.method !== 'get') {
        const payload = encodeURI(JSON.stringify(config.data));
        const encryptedData = encryptPayload(payload);
        
        if (!encryptedData) {
          console.error("Encryption failed!");
          throw new Error("Encryption failed");
        }
        
        config.data = { data: encryptedData };
      }
      return config;
    } catch (error) {
      console.error("Request encryption error:", error);
      captureException(error, {
        tags: { area: 'donate-api-request' },
        extras: {
          url: config.url,
          method: config.method,
        },
      });
      return Promise.reject(error);
    }
  },
  (error) => Promise.reject(error)
);

// Response interceptor - decrypt response body
api.interceptors.response.use(
  (response) => {
    try {
      // Decrypt the encrypted response data
      if (response.data?.data?.encryptedData) {
        const decryptedData = decryptResponseData(response.data.data.encryptedData);
        if (decryptedData) {
          response.data.data = JSON.parse(decodeURI(decryptedData));
        }
      }
      return response;
    } catch (error) {
      console.error("Response decryption error:", error);
      captureException(error, {
        tags: { area: 'donate-api-response' },
        extras: {
          url: response.config?.url,
          method: response.config?.method,
          status: response.status,
        },
      });
      return response;
    }
  },
  (error) => {
    // Try to decrypt error response if present
    try {
      if (error.response?.data?.data?.encryptedData) {
        const decryptedData = decryptResponseData(error.response.data.data.encryptedData);
        if (decryptedData) {
          error.response.data.data = JSON.parse(decodeURI(decryptedData));
        }
      }
    } catch (decryptError) {
      console.error("Error response decryption failed:", decryptError);
      captureException(decryptError, {
        tags: { area: 'donate-api-error-decrypt' },
        extras: {
          url: error?.config?.url,
          method: error?.config?.method,
          status: error?.response?.status,
        },
      });
    }

    captureException(error, {
      tags: { area: 'donate-api-error' },
      extras: {
        url: error?.config?.url,
        method: error?.config?.method,
        status: error?.response?.status,
      },
    });
    return Promise.reject(error);
  }
);

// Error handler
const handleError = (error: AxiosError): ApiError => {
  if (error.response) {
    const data = error.response.data as any;
    return {
      message: data.message || 'An error occurred',
      code: data.code,
      status: error.response.status,
    };
  }
  return {
    message: error.message || 'Network error',
    code: 'NETWORK_ERROR',
  };
};

// Get public program details (no auth required)
export const getProgramDetails = async (programId: string): Promise<Program> => {
  try {
    const response = await api.get(`/public/program/${programId}`);
    return response.data.data;
  } catch (error) {
    throw handleError(error as AxiosError);
  }
};

// Get public fundraiser details (used when visiting /fundraiser/:id)
export const getFundraiserDetails = async (fundraiserId: string): Promise<any> => {
  try {
    const response = await api.get(`/public/fundraiser/${fundraiserId}`);
    return response.data.data;
  } catch (error) {
    throw handleError(error as AxiosError);
  }
};

// Calculate Stripe transaction fee
export const getStripeFee = async (
  donationAmount: number,
  donationToGFTL: number,
  programId: string
): Promise<{ stripeFee: number; stripeFeeMin: number; stripeFeeMax: number }> => {
  try {
    const response = await api.get('/public/donation/stripe-fee', {
      params: {
        donationAmount,
        donationToGFTL,
        programId,
      },
    });
    return response.data.data;
  } catch (error) {
    throw handleError(error as AxiosError);
  }
};

// Create payment intent for anonymous donation
export const createAnonymousPaymentIntent = async (
  programId: string,
  donationData: DonationFormData,
  totalAmount: number
  , fundRaiseId?: string
): Promise<PaymentIntentResponse> => {
  try {
    const response = await api.post('/public/donation/create-payment-intent', {
      programId,
      email: donationData.email,
      name: donationData.name,
      donationAmount: donationData.amount,
      donationToGFTL: donationData.donationToGFTL,
      totalAmount,
      isGiftAid: donationData.isGiftAid,
      houseNo: donationData.houseNo,
      postCode: donationData.postCode,
      anonymousDonation: donationData.anonymousDonation,
      donationNote: donationData.donationNote,
      ...(fundRaiseId ? { fundRaiseId } : {}),
      donationType: 'onetime',
      donationTo: 'program',
    });
    return response.data.data;
  } catch (error) {
    throw handleError(error as AxiosError);
  }
};

// Confirm donation was successful
export const confirmDonation = async (
  paymentIntentId: string,
  donationId: string
): Promise<{ success: boolean; receiptNumber: string }> => {
  try {
    const response = await api.post('/public/donation/confirm', {
      paymentIntentId,
      donationId,
    });
    return response.data.data;
  } catch (error) {
    throw handleError(error as AxiosError);
  }
};

// Get donation status
export const getDonationStatus = async (
  paymentIntentId: string
): Promise<{ status: string; donationId: string }> => {
  try {
    const response = await api.get(`/public/donation/status/${paymentIntentId}`);
    return response.data.data;
  } catch (error) {
    throw handleError(error as AxiosError);
  }
};

export default api;
